import * as React from "react";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { WindowKey } from "@bizform/type/model/model/WindowKey.model";
import { ComponentHelper, Debug } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../../bf_component/AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


/*
 * fitPanel이 manual인 경우 전달받은 width & height가 기준이 된다.
 */
type Props = {
    customId?: string;
    name?: string;
    title?: string;
    src?: string;
    frameBorder?: string; /* deprecated in HTML 5 */
    marginWidth?: number; /* deprecated in HTML 5 */
    marginHeight?: number; /* deprecated in HTML 5 */
    scrolling?: string; /* deprecated in HTML 5 */
    className?: string;
    style?: string;
};

type WrappedCompState = Props & {
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
};

/**
 * Iframe component element
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfIframePanel extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_FEgd5mu8l0VTePWhCMSppTBxItWACer",
        category: ComponentCategory.PANEL,
        label: I18n.layout.iframe.label,
        description: I18n.layout.iframe.description,
        icon: "",
        property: null
    };

    focus(): void {
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfIframePanel)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        if (this && this.destroyDone) {
            this.destroyDone();
        }
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _ref: object;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
            }
        };

        this._setRef = this._setRef.bind(this);
    }

    componentWillUnmount(): void {
        this._ref = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                case ComponentElementDisplayMode.PRINT:
                    return this._render4ReadOnly();
                default:
                    return null;
            }
        }
    }

    /**
     * newProps에 기본값을 세팅한다.
     * defaultProps를 이용할 수 있으나 id 같은 경우는 각각의 인스턴스마다
     * 다르게 기본값을 부여하게 되므로 이 메소드를 이용하여 기본값을 부여한다.
     *
     * @param newProps
     * @private
     */
    private _setPropsWithDefault(newProps: Props): Props {
        newProps.customId = newProps.customId || this.elementId;
        return newProps;
    }

    private _render4ReadOnly(): React.ReactNode {
        const tmpProps = this.state.wrappedCompState;
        const newProps = this._setPropsWithDefault(tmpProps);
        const style = newProps.style ? (typeof newProps.style === "string" ? JSON.parse(newProps.style) : newProps.style) : {};

        if (window[WindowKey.DESIGNMODE]) {
            const attr = {
                style: {
                    "top": "0",
                    "right": "0",
                    "bottom": "0",
                    "left": "0",
                    "backgroundColor": "rgba(0, 255, 0, 0.2)"
                }
            };
            return (
                <React.Fragment>
                    <div className={"position-relative w-100 h-100"}>
                        <iframe
                            bf-custom-id={newProps.customId}
                            name={newProps.name}
                            title={newProps.title}
                            src={newProps.src}
                            frameBorder={newProps.frameBorder}
                            marginWidth={newProps.marginWidth}
                            marginHeight={newProps.marginHeight}
                            scrolling={newProps.scrolling}
                            className={newProps.className}
                            style={style}
                            ref={this._setRef}
                        ></iframe>
                        <div className={"position-absolute"} { ...attr }>&nbsp;</div>
                    </div>
                </React.Fragment>
            );
        } else {
            return (
                <iframe
                    bf-custom-id={newProps.customId}
                    name={newProps.name}
                    title={newProps.title}
                    src={newProps.src}
                    frameBorder={newProps.frameBorder}
                    marginWidth={newProps.marginWidth}
                    marginHeight={newProps.marginHeight}
                    scrolling={newProps.scrolling}
                    className={newProps.className}
                    style={style}
                    ref={this._setRef}
                ></iframe>
            );
        }
    }

    private _setRef(ref): void {
        if (ref) {
            this._ref = ref;
            this.readyComponent();
        }
    }
}